/**
 * FE-024 — what the rest of the app needs to know about the bottom nav bar:
 * whether it is on screen (so the AIRA widget docks into its cut-out and pages
 * pad for it) and whether it is currently slid away by scrolling.
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useFairBottomNav = defineStore('fairBottomNav', () => {
  const mounted = ref(false)
  /** Slid away by scrolling. */
  const scrolledAway = ref(false)
  /**
   * Something else is using the bar's place right now — the PDP price sticky,
   * for instance. One thing at a time at the bottom: while any claim is held
   * the bar (and the docked AIRA ball) stay out of the way and the scroll
   * logic does not fight back.
   */
  const claims = ref<Set<string>>(new Set())

  const claimed = computed(() => claims.value.size > 0)
  const hidden = computed(() => scrolledAway.value || claimed.value)

  /** The AIRA ball sits in the bar's cut-out while the bar is mounted. */
  const docked = computed(() => mounted.value)

  function setMounted(value: boolean) {
    mounted.value = value
    if (!value) scrolledAway.value = false
  }

  function setHidden(value: boolean) {
    scrolledAway.value = value
  }

  function claim(id: string) {
    const next = new Set(claims.value)
    next.add(id)
    claims.value = next
  }

  function release(id: string) {
    if (!claims.value.has(id)) return
    const next = new Set(claims.value)
    next.delete(id)
    claims.value = next
    // The place is free again: the bar comes back regardless of how the page
    // was scrolled while someone else held it.
    if (next.size === 0) scrolledAway.value = false
  }

  return { mounted, hidden, claimed, docked, setMounted, setHidden, claim, release }
})
